// The canvas's right panel (F3-1): the settings of the selected node or line. After the_Kit's NodeInspector and
// EdgeInspector, with fixed fields per node kind instead of a schema form. The API node's inputs are shown, never typed:
// they are read from the endpoint's handler (PI info, an earlier step, the lender's variables).
import { Trash2 } from 'lucide-react'
import { Field } from '@/kit/ui'
import { TEST_APPLICANT } from '@/fixtures/code'
import { ANSWER_KINDS, CONDITION_FIELDS, OPS, defaultMessage, edgesFrom, outputsOf } from '@/lib/flow'
import { endpointLabel, endpointOf, inputsOf } from '@/lib/endpoints'
import type { AnswerKind, Batch, Condition, FlowEdge, FlowNode } from '@/types'

export function ConditionEditor({ value, onChange, disabled, idPrefix }: { value: Condition; onChange: (c: Condition) => void; disabled: boolean; idPrefix: string }) {
  const known = CONDITION_FIELDS.some((f) => f.value === value.field)
  return (
    <div className="space-y-2">
      <Field label="Field">
        {(id) => (
          <select id={id} className="input" disabled={disabled} value={known ? value.field : '__custom'} onChange={(e) => onChange({ ...value, field: e.target.value === '__custom' ? 'steps.' : e.target.value })}>
            {CONDITION_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            <option value="__custom">another field…</option>
          </select>
        )}
      </Field>
      {!known && (
        <Field label="Field path" help="e.g. steps.offers (the outcome of the step named offers), prev.status">
          {(id) => <input id={`${idPrefix}-${id}`} className="input mono" disabled={disabled} value={value.field} onChange={(e) => onChange({ ...value, field: e.target.value })} />}
        </Field>
      )}
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <Field label="Operator">
          {(id) => <select id={id} className="input" disabled={disabled} value={value.op} onChange={(e) => onChange({ ...value, op: e.target.value as Condition['op'] })}>{OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>}
        </Field>
        {value.op !== 'exists' && value.op !== 'missing' && (
          <Field label="Value">
            {(id) => <input id={id} className="input mono" disabled={disabled} value={value.value} onChange={(e) => onChange({ ...value, value: e.target.value })} placeholder="LOW_INCOME" />}
          </Field>
        )}
      </div>
    </div>
  )
}

const EMPTY_COND: Condition = { field: 'prev.reply_code', op: 'eq', value: '' }

export function Inspector({ nodes, edges, batch, readOnly, selection, onChange, onClose, waitPerCallS, tokenRoutes }: {
  nodes: FlowNode[]
  edges: FlowEdge[]
  batch: Batch | undefined
  readOnly: boolean
  selection: { nodeId?: string; edgeId?: string }
  onChange: (nodes: FlowNode[], edges: FlowEdge[]) => void
  onClose: () => void
  waitPerCallS: number
  tokenRoutes: string[]
}) {
  const node = selection.nodeId ? nodes.find((n) => n.id === selection.nodeId) : undefined
  const edge = selection.edgeId ? edges.find((e) => e.id === selection.edgeId) : undefined
  const setNode = (patch: Partial<FlowNode>) => node && onChange(nodes.map((n) => (n.id === node.id ? { ...n, ...patch } : n)), edges)
  const setEdge = (patch: Partial<FlowEdge>) => edge && onChange(nodes, edges.map((e) => (e.id === edge.id ? { ...e, ...patch } : e)))
  const name = (id: string) => nodes.find((n) => n.id === id)?.name ?? id

  if (!node && !edge) return <p className="text-xs text-muted">Click a node or a line on the canvas for its settings.{readOnly ? '' : ' Drag from a port on the right of a node to another node to join them.'}</p>

  if (edge) {
    return (
      <div className="space-y-3" data-testid="edge-inspector">
        <p className="text-xs"><b>{name(edge.from)}</b> · port <code>{edge.when}</code> → <b>{name(edge.to)}</b></p>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" disabled={readOnly} checked={!!edge.condition} onChange={(e) => setEdge({ condition: e.target.checked ? EMPTY_COND : null })} />
          Only when a condition holds (dashed line, tried before the plain line of the same port)
        </label>
        {edge.condition && <ConditionEditor idPrefix="edge" value={edge.condition} disabled={readOnly} onChange={(c) => setEdge({ condition: c })} />}
        {!readOnly && <button type="button" className="btn btn-ghost btn-sm !text-red-700 dark:!text-red-300" onClick={() => { onChange(nodes, edges.filter((e) => e.id !== edge.id)); onClose() }}><Trash2 size={11} /> Delete line</button>}
      </div>
    )
  }

  const n = node!
  const ep = n.kind === 'api' && n.endpoint && batch ? endpointOf(batch.manifest, n.endpoint) : undefined
  const code = ep ? batch?.files.find((f) => f.path === ep.file)?.content ?? '' : ''
  const ins = code ? inputsOf(code) : null
  const routes = (batch?.manifest.endpoints ?? []).filter((e) => !e.tokenSource && !tokenRoutes.includes(e.path)).map((e) => e.path)

  return (
    <div className="space-y-3" data-testid="node-inspector">
      <Field label="Name">
        {(id) => <input id={id} className="input" disabled={readOnly || n.kind === 'start'} value={n.name} onChange={(e) => setNode({ name: e.target.value })} />}
      </Field>

      {n.kind === 'start' && (
        <div className="text-2xs text-muted">
          <p className="mb-1">The standard input (G10): the PI info our website or app sends with <code>endpoint_id</code>. Every API node takes what it needs from here by itself.</p>
          <code className="block whitespace-normal">{Object.keys(TEST_APPLICANT).join(' · ')}</code>
        </div>
      )}

      {n.kind === 'api' && (
        <>
          <Field label="Endpoint" help={tokenRoutes.length ? `${tokenRoutes.join(', ')} is the token source: the Gateway calls it, not a workflow` : ep ? `file ${ep.file}` : undefined}>
            {(id) => (
              <select id={id} className="input mono" disabled={readOnly} value={n.endpoint ?? ''} onChange={(e) => setNode({ endpoint: e.target.value, name: n.name === endpointLabel(n.endpoint ?? '').split('/').pop() ? endpointLabel(e.target.value).split('/').pop() : n.name })}>
                {!routes.includes(n.endpoint ?? '') && <option value={n.endpoint ?? ''}>{n.endpoint ?? '(choose)'} (not in this batch)</option>}
                {routes.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Wait for the lender (s)" help={`empty = ${waitPerCallS} s (the workflow's)`}>
              {(id) => <input id={id} className="input" inputMode="numeric" disabled={readOnly} value={n.waitS ?? ''} onChange={(e) => setNode({ waitS: e.target.value === '' ? null : Math.max(1, Number(e.target.value) || 1) })} />}
            </Field>
            <Field label="Retries on error">
              {(id) => <input id={id} className="input" inputMode="numeric" disabled={readOnly} value={n.retries ?? 0} onChange={(e) => setNode({ retries: Math.max(0, Math.min(3, Number(e.target.value) || 0)) })} />}
            </Field>
          </div>
          {ins && (
            <div className="rounded border border-slate-200 p-2 text-2xs dark:border-slate-700">
              <div className="mb-1 font-semibold">Input mapping (read from the handler, automatic)</div>
              <ul className="space-y-0.5">
                {ins.applicant.map((f) => <li key={f}><code>applicant.{f}</code> ← PI info</li>)}
                {ins.prev.map((f) => <li key={f}><code>ctx.prev.{f}</code> ← the earlier step's outcome</li>)}
                {ins.env.map((f) => <li key={f}><code>ctx.env.{f}</code> ← Env variables tab</li>)}
                {!ins.applicant.length && !ins.prev.length && !ins.env.length && <li className="text-muted">takes nothing from the applicant</li>}
              </ul>
            </div>
          )}
          <PortList node={n} nodes={nodes} edges={edges} batch={batch} />
        </>
      )}

      {n.kind === 'decision' && (
        <>
          <p className="text-2xs text-muted">No code: a field, an operator and a value. <b>yes</b> when it holds, <b>no</b> otherwise.</p>
          <ConditionEditor idPrefix="node" value={n.condition ?? EMPTY_COND} disabled={readOnly} onChange={(c) => setNode({ condition: c })} />
          <PortList node={n} nodes={nodes} edges={edges} batch={batch} />
        </>
      )}

      {n.kind === 'wait' && (
        <Field label="Wait (s)" help="Counted against the total wait; when it is used up the answer is pending">
          {(id) => <input id={id} className="input" inputMode="numeric" disabled={readOnly} value={n.seconds ?? 0} onChange={(e) => setNode({ seconds: Math.max(1, Number(e.target.value) || 1), name: `Wait ${Math.max(1, Number(e.target.value) || 1)} s` })} />}
        </Field>
      )}

      {n.kind === 'answer' && (
        <>
          <Field label="Standard answer">
            {(id) => (
              <select id={id} className="input" disabled={readOnly} value={n.answer ?? 'error'} onChange={(e) => { const k = e.target.value as AnswerKind; setNode({ answer: k, name: `Answer · ${k}`, message: n.message === defaultMessage(n.answer ?? 'error') ? defaultMessage(k) : n.message }) }}>
                {ANSWER_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            )}
          </Field>
          <Field label="Message for our user" help="{lender} becomes the lender's name; the app shows it as is">
            {(id) => <textarea id={id} className="input min-h-20" disabled={readOnly} value={n.message ?? ''} onChange={(e) => setNode({ message: e.target.value })} />}
          </Field>
          <p className="text-2xs text-muted">The answer also carries the lender reference, the offers (offer only), the continue link (the lender's, else Lender info's web link) and the lender's reply masked with a log id.</p>
        </>
      )}

      {!readOnly && n.kind !== 'start' && (
        <button type="button" className="btn btn-ghost btn-sm !text-red-700 dark:!text-red-300" onClick={() => { onChange(nodes.filter((x) => x.id !== n.id), edges.filter((e) => e.from !== n.id && e.to !== n.id)); onClose() }}>
          <Trash2 size={11} /> Delete node and its lines
        </button>
      )}
    </div>
  )
}

function PortList({ node, nodes, edges, batch }: { node: FlowNode; nodes: FlowNode[]; edges: FlowEdge[]; batch: Batch | undefined }) {
  const name = (id: string) => nodes.find((n) => n.id === id)?.name ?? id
  return (
    <div className="text-2xs">
      <div className="mb-1 font-semibold">Ports</div>
      <ul className="space-y-0.5">
        {outputsOf(node, batch).map((p) => {
          const out = edgesFrom({ edges }, node.id, p)
          return (
            <li key={p} className="flex gap-1">
              <code className={p === 'error' ? 'text-red-600' : ''}>{p}</code>
              <span className="text-muted">→</span>
              {out.length ? <span>{out.map((e) => `${name(e.to)}${e.condition ? ' (if …)' : ''}`).join(', ')}</span> : <span className="text-red-600">no line (dead end)</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
